import { Close } from "@mui/icons-material";
import { Box, Button, Grid } from "@mui/material";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useWebSocket } from "../../../../contexts/WebSocketContext";
import { useGeneratePostPartMutation } from "../../../../redux/slices/featuresApiSlice";
import {
  logGenerationError,
  logGenerationStart,
} from "../../../../utils/sseDebugger";
import { useGenerationErrorHandling } from "../../../errorHandling";
import { trackPlatformUsed } from "../../../trackers";
import { GenerationButton } from "../../FeaturesTemplate/FeaturesFields/GenerationButton";
import GlobalTextField from "../../FeaturesTemplate/FeaturesFields/GlobalTextField";
import RewriteModal from "../../SidePanel/global-rewrite-modal/rewrite-modal";
import { useSelector } from "react-redux";
import { getSessionUUID } from "../../../../utils/uuidManager";

const PartModal = ({ editor, partData, isPartGenerated }) => {
  const { t } = useTranslation();
  const [customize, setCustomize] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const userInfo = useSelector((state) => state?.auth.userInfo);

  // Store the initial isPartGenerated state to prevent it from changing during streaming
  const [initialIsPartGenerated] = useState(isPartGenerated);

  const [generatePostPart, { isLoading }] = useGeneratePostPartMutation();
  const handleGenerationError = useGenerationErrorHandling();
  const {
    openPartRewrite,
    setOpenPartRewrite,
    setOpenPartModal,
    bufferRef,
    cleanGeneratedContent,
    ws,
    reconnectSSE,
  } = useWebSocket();

  const formikProps = useFormikContext();

  const isEmptyHTML = (html) => {
    const text = html
      ?.replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();
    return !text;
  };

  const removeFirstAndLastH2Tags = (html) => {
    if (!html) return html;
    let cleaned = html.replace(/<h2[^>]*>.*?<\/h2>/i, "").trim();
    cleaned = cleaned
      .replace(/(<h2[^>]*>.*<\/h2>)(?![\s\S]*<h2[^>]*>.*<\/h2>)/i, "")
      .trim();
    return cleaned;
  };

  const handleGenerate = async () => {
    trackPlatformUsed("part", "ai_generation");
    setIsGenerating(true);

    // Reset buffer before starting new generation
    bufferRef.current = "";

    // Check if SSE connection is available
    if (!ws || ws.readyState !== EventSource.OPEN) {
      reconnectSSE();
      toast.warning("Connection not ready. Please try again in a moment.");
      setIsGenerating(false);
      return;
    }

    const el = document.createElement("div");
    el.innerHTML = partData.content;
    const result = cleanUpElementToJSON(el);

    try {
      const payload = {
        id: formikProps.values.id || null,
        style_id: formikProps.values.style_id || null,
        company_id: formikProps.values.company_id || null,
        product_id: formikProps.values.product_id || null,
        subject: formikProps.values.subject,
        seo_request: formikProps.values.seo_request,
        nb_words: formikProps.values.nb_words,
        outline: formikProps.values.outline,
        part_name: isEmptyHTML(result.part_name)
          ? removeFirstAndLastH2Tags(formikProps.values.outline)
          : result.part_name,
      };

      if (customize && formikProps.values.customize_prompt)
        payload["customize_prompt"] = formikProps.values.customize_prompt;

      if (!initialIsPartGenerated) payload["isRewrite"] = false;

      logGenerationStart("part", { ...payload, stream: true });
      const uuid = userInfo?.uuid || getSessionUUID();
      const res = await generatePostPart({ ...payload, uuid, stream: true }).unwrap();

      if (res?.status !== 200) {
        toast.error("Something went wrong with generation");
        return;
      }

      // For streaming, the content will be handled by WebSocket context
      // Only handle non-streaming fallback here
      if (!res?.stream && res?.data) {
        const cleanedContent = cleanGeneratedContent(res?.data);

        if (!cleanedContent || cleanedContent === "undefined") {
          toast.warning("No valid content generated. Please try again.");
          return;
        }

        const contentRewrite = formikProps.values.content_rewrite;

        if (initialIsPartGenerated) {
          formikProps.setFieldValue("content_rewrite", {
            ...contentRewrite,
            [partData.title]: cleanedContent,
          });
        } else {
          editor
            .chain()
            .focus()
            .deleteRange({
              from: partData.startOfPart,
              to: editor.state.selection.$from.end(2),
            })
            .insertContentAt(partData.startOfPart, cleanedContent)
            .run();
        }
      }
    } catch (error) {
      logGenerationError("part", error);
      handleGenerationError(error);
      toast.error("Generation failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRewrite = async () => {
    trackPlatformUsed("part", "ai_rewrite");
    setIsGenerating(true);

    // Reset buffer before starting new generation
    bufferRef.current = "";

    const el = document.createElement("div");
    el.innerHTML = partData.content;
    const result = cleanUpElementToJSON(el);

    try {
      const payload = {
        id: formikProps.values.id || null,
        style_id: formikProps.values.style_id || null,
        company_id: formikProps.values.company_id || null,
        product_id: formikProps.values.product_id || null,
        subject: formikProps.values.subject,
        seo_request: formikProps.values.seo_request,
        nb_words: formikProps.values.nb_words,
        outline: formikProps.values.outline,
        part_name: result.part_name,
        part: result.part,
        isRewrite: true,
      };

      if (customize && formikProps.values.customize_prompt)
        payload["customize_prompt"] = formikProps.values.customize_prompt;

      const uuid = userInfo?.uuid || getSessionUUID();
      const res = await generatePostPart({ ...payload, uuid, stream: true }).unwrap();

      if (res?.status !== 200) {
        toast.error("Something went wrong with rewrite generation");
        return;
      }

      // For streaming, the content will be handled by WebSocket context
      // Only handle non-streaming fallback here
      if (!res?.stream && res?.data) {
        const cleanedContent = cleanGeneratedContent(res?.data);

        if (!cleanedContent || cleanedContent === "undefined") {
          toast.warning("No valid rewritten content received.");
          return;
        }

        // The WebSocket context will handle whether to show modal or insert directly
        // based on the content analysis
      }
    } catch (error) {
      handleGenerationError(error);
      toast.error("Rewrite generation failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const saveSelectedVersion = (data) => {
    if (isGenerating) return;

    // Aggressive content cleaning to remove ALL leading and trailing whitespace
    let finalCleanedData = cleanGeneratedContent(data);

    // Step 1: Remove all leading and trailing whitespace from the raw HTML
    finalCleanedData = finalCleanedData.trim();

    // Step 2: Remove leading empty HTML elements using regex
    finalCleanedData = finalCleanedData.replace(
      /^(\s*<(p|div|br)[^>]*>\s*<\/(p|div)>\s*)+/gi,
      ""
    );
    finalCleanedData = finalCleanedData.replace(/^(\s*<br[^>]*>\s*)+/gi, "");

    // Step 3: Remove trailing empty HTML elements using regex
    finalCleanedData = finalCleanedData.replace(
      /(\s*<(p|div|br)[^>]*>\s*<\/(p|div)>\s*)+$/gi,
      ""
    );
    finalCleanedData = finalCleanedData.replace(/(\s*<br[^>]*>\s*)+$/gi, "");

    // Step 4: Use DOM manipulation for more thorough cleaning
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = finalCleanedData;

    // Step 5: Aggressively remove leading empty elements
    let changed = true;
    while (changed) {
      changed = false;
      const firstChild = tempDiv.firstChild;

      if (firstChild) {
        // Remove empty text nodes at the beginning
        if (
          firstChild.nodeType === Node.TEXT_NODE &&
          !firstChild.textContent.trim()
        ) {
          tempDiv.removeChild(firstChild);
          changed = true;
        }
        // Remove empty elements at the beginning
        else if (firstChild.nodeType === Node.ELEMENT_NODE) {
          const tagName = firstChild.tagName.toLowerCase();
          if (
            (tagName === "p" || tagName === "div") &&
            !firstChild.textContent.trim()
          ) {
            tempDiv.removeChild(firstChild);
            changed = true;
          } else if (tagName === "br") {
            tempDiv.removeChild(firstChild);
            changed = true;
          }
        }
      }
    }

    // Step 6: Aggressively remove trailing empty elements
    changed = true;
    while (changed) {
      changed = false;
      const lastChild = tempDiv.lastChild;

      if (lastChild) {
        // Remove empty text nodes at the end
        if (
          lastChild.nodeType === Node.TEXT_NODE &&
          !lastChild.textContent.trim()
        ) {
          tempDiv.removeChild(lastChild);
          changed = true;
        }
        // Remove empty elements at the end
        else if (lastChild.nodeType === Node.ELEMENT_NODE) {
          const tagName = lastChild.tagName.toLowerCase();
          if (
            (tagName === "p" || tagName === "div") &&
            !lastChild.textContent.trim()
          ) {
            tempDiv.removeChild(lastChild);
            changed = true;
          } else if (tagName === "br") {
            tempDiv.removeChild(lastChild);
            changed = true;
          }
        }
      }
    }

    // Step 7: Final cleanup
    finalCleanedData = tempDiv.innerHTML.trim();

    // Step 8: Remove any remaining leading/trailing whitespace patterns
    finalCleanedData = finalCleanedData.replace(/^\s+|\s+$/g, "");
    finalCleanedData = finalCleanedData.replace(
      /^(<br\s*\/?>)+|(<br\s*\/?>)+$/gi,
      ""
    );
    finalCleanedData = finalCleanedData.replace(
      /^(<p>\s*<\/p>)+|(<p>\s*<\/p>)+$/gi,
      ""
    );

    editor
      .chain()
      .focus()
      .deleteRange({ from: partData.startOfPart, to: partData.endOfPart })
      .insertContentAt(partData.startOfPart, finalCleanedData)
      .run();

    const contentRewrite = { ...formikProps.values.content_rewrite };
    delete contentRewrite[partData.title];
    formikProps.setFieldValue("content_rewrite", {});
    formikProps.setFieldValue("rephrase_rewrite", "");

    setOpenPartRewrite(false);
    setOpenPartModal(false);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        height: "100vh",
        width: {
          lg: "calc(100vw - 500px)",
          md: "calc(100vw - 350px)",
          xs: "100%",
        },
        display: "flex",
        mt: { md: "48px !important", xs: "unset" },
        left: 0,
        top: 0,
        bgcolor: "#000000a1",
        zIndex: 99999,
      }}
    >
      <div className="m-auto bg-white rounded-xl w-[80%] max-w-[500px]">
        <div className="flex justify-between p-4 items-center border-b-[3px] border-slate-200">
          <p>
            {initialIsPartGenerated
              ? t("Rewrite-the-part", "Rewrite the part")
              : t("Generate-the-part", "Generate the part")}
          </p>
          <Close
            onClick={() => {
              setCustomize(false);
              formikProps.setFieldValue("customize_prompt", "");
              formikProps.setFieldTouched("customize_prompt", false);
              setOpenPartModal(false);
            }}
            className="cursor-pointer"
            sx={{ fontSize: 32 }}
          />
        </div>

        <div className="p-4 mt-3">
          {partData && partData.titleText && (
            <div>
              <p className="block text-sm font-normal text-gray-700">
                {t("Selected-part", "Selected part")}:
              </p>
              <div className="w-full p-2 mt-1 border border-black/20 rounded-md max-h-48 overflow-auto">
                <p className="font-semibold mb-0">{`(H${partData.level}) ${partData.titleText}`}</p>
              </div>
            </div>
          )}

          {/* Customize toggle */}
          <div className="mt-4 inline-flex items-center h-6">
            <label className="inline-flex items-center cursor-pointer relative">
              <input
                type="checkbox"
                name="customize"
                checked={customize}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setCustomize(checked);
                  if (!checked) {
                    formikProps.setFieldValue("customize_prompt", "");
                  }
                }}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray_local-400 peer-checked:bg-blue-50 transition-colors duration-200 rounded-full peer peer-checked:after:translate-x-full after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform after:box-border"></div>
            </label>
            <span className="ms-3 text-sm">
              {t("custom-instructions", "Instructions personnalisées")}
            </span>
          </div>

          {/* Customize field */}
          {customize && (
            <Grid item lg={12} xs={12}>
              <p>
                {t("Instruction", "Instruction")}
                <span className="text-red-500 ml-1">*</span>
              </p>
              <GlobalTextField
                formikProps={formikProps}
                name={"customize_prompt"}
                placeholder={t(
                  "Ex: Make it more formal",
                  "Ex: Rends-le plus formel"
                )}
                isRequired={true}
                multipleLines={true}
                maxLength={1000}
              />
            </Grid>
          )}

          <div className="flex items-center mt-3 gap-4 flex-wrap">
            {!initialIsPartGenerated && (
              <GenerationButton
                formikProps={formikProps}
                text={t("Generate-the-part")}
                handleGenerate={handleGenerate}
                isLoading={isLoading}
                platform="openai"
                customize={customize}
              />
            )}

            {initialIsPartGenerated && (
              <GenerationButton
                formikProps={formikProps}
                text={t("Rewrite-the-part", "Rewrite the part")}
                handleGenerate={handleRewrite}
                isLoading={isLoading}
                platform="openai"
                customize={customize}
              />
            )}

            {formikProps.values.content_rewrite[partData.title] ? (
              <Button
                variant="contained"
                className="!bg-violet-400 hover:!bg-violet-500 text-white !normal-case"
                onClick={() => setOpenPartRewrite(true)}
              >
                View rewritten version
              </Button>
            ) : null}
          </div>

          {openPartRewrite &&
            formikProps.values.content_rewrite[partData.title] &&
            formikProps.values.content_rewrite[partData.title] !==
            "undefined" && (
              <div className="w-[calc(100vw-350px)] xl:w-[calc(100vw-500px)] fixed z-[99999] mt-[48px] top-0 bg-[#00000029] left-0 flex h-screen">
                <RewriteModal
                  previous={partData.content}
                  rewritten={formikProps.values.content_rewrite[partData.title]}
                  save={saveSelectedVersion}
                  setOpenModal={setOpenPartRewrite}
                  label="Part (rewrite)"
                  rewriteFieldName="part_rewrite"
                  partTitle={partData.title}
                  isSaveDisabled={isGenerating}
                />
              </div>
            )}
        </div>
      </div>
    </Box>
  );
};

// Helper
function cleanUpElementToJSON(rootElement) {
  if (!rootElement) return { part_name: "", part: "" };

  function cleanPartName(element) {
    const cloned = element.cloneNode(true);
    cloned.querySelectorAll("p, br").forEach((el) => el.remove());
    return cloned.innerHTML;
  }

  function cleanPart(element) {
    const cloned = element.cloneNode(true);
    cloned.querySelectorAll("p").forEach((el) => {
      if (!el.textContent.trim()) el.remove();
    });
    return cloned.innerHTML;
  }

  return {
    part_name: cleanPartName(rootElement),
    part: cleanPart(rootElement),
  };
}

export default PartModal;
